import React from 'react';
import { get } from 'lodash';

import { useStyles } from '~/assets/styles';
import AdjusterSection from '~/components/communications/CommunicationCard/AdjusterSection';
import AttachToContactSection from '~/components/communications/CommunicationCard/AttachToContactSection';
import CommunicationCardHeader from '~/components/communications/CommunicationCard/CommunicationCardHeader';
import CommunicationDateTimeSection from '~/components/communications/CommunicationCard/CommunicationDateTimeSection';
import CommunicationIdentifierSection from '~/components/communications/CommunicationCard/CommunicationIdentifierSection';
import ContactSection from '~/components/communications/CommunicationCard/ContactSection';
import { formatCallDuration, getCommunicationIconSettings } from '~/components/communications/CommunicationCard/utils';
import { getCommunicationIcon } from '~/components/communications/CommunicationUtils';
import { Caption, Heading, Text } from '~/components/core';
import Tooltip from '~/components/core/Atomic/Tooltip';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import type { PhoneCallCommunication } from '~/server_shared/swagger/data-contracts';
import { RECIPIENTS_TYPES } from '~/Types';
import cn from '~/Utils/cn';

import type { CommunicationType, EmailCommunication } from '../types';

import AutomaticEmailMessageSection from './AutomaticEmailMessageSection';
import type { CommunicationFullHeaderProps } from './types';

const isEmailCommunication = (communication: CommunicationType): communication is EmailCommunication =>
  communication.channel === 'email';

const isPhoneCallCommunication = (communication: CommunicationType): communication is PhoneCallCommunication =>
  communication?.channel === 'phone_call';

const CommunicationFullHeader: React.FC<CommunicationFullHeaderProps> = ({
  communication,
  contact,
  communicationIdentifier,
  additionalHeaderProp,
  communicationClaimId,
  channel,
  direction,
  isView,
  onCommunicationUpdate,
  newPhoneCommunicationUI = false,
  isNew = false,
  isDocument = false,
  categoryChips,
}) => {
  const { classes } = useStyles();
  if (isView || (!isView && newPhoneCommunicationUI)) {
    const settings = getCommunicationIconSettings(communication, channel);
    const isContactAttached = !!contact?.id;

    const isMarkedNoAnswer = isPhoneCallCommunication(communication) && communication.is_no_answer;
    const isCallInactive = !additionalHeaderProp?.isCallActive;
    const isPersistedWithDuration = !isNew || (isNew && (additionalHeaderProp?.callDurationSec ?? 0) > 0);

    const isUnansweredPhoneCall =
      isPhoneCallCommunication(communication) &&
      isMarkedNoAnswer &&
      isCallInactive &&
      isPersistedWithDuration &&
      !isDocument;

    return (
      <div className="pb-20">
        <div
          className={cn('flex items-start justify-between rounded-lg border border-solid p-12', settings.borderColor)}
        >
          <div className="flex min-w-[40%] flex-1 items-center overflow-hidden">
            {getCommunicationIcon({
              communication,
              isUnansweredPhoneCall,
            })}
            <div className="ml-8 min-w-0 flex-1">
              {isContactAttached ? (
                <ContactSection
                  contactId={contact.id}
                  contactRole={contact?.role}
                  contactFullName={contact?.full_name}
                />
              ) : communication?.id ? (
                <AttachToContactSection
                  communicationId={communication.id}
                  communicationClaimId={communicationClaimId}
                  communicationChannel={channel}
                  onAttachToContact={onCommunicationUpdate}
                />
              ) : null}
              <CommunicationIdentifierSection channel={channel} communicationIdentifier={communicationIdentifier} />
              <AdjusterSection
                internalEmailAddress={
                  isEmailCommunication(communication) ? communication.internal_email_address : undefined
                }
                adjusterRecipientType={
                  isEmailCommunication(communication) ? communication.adjuster_recipient_type : undefined
                }
                adjuster={communication.adjuster}
                direction={direction}
              />
            </div>
          </div>
          <div className="flex max-w-[40%] flex-col items-end gap-8">
            <div className="flex shrink-0 flex-col">
              <CommunicationDateTimeSection communicationDateTime={communication.datetime} />
              {isEmailCommunication(communication) && communication.is_acknowledgment_email ? (
                <AutomaticEmailMessageSection />
              ) : null}

              {isPhoneCallCommunication(communication) ? (
                <div className="flex items-center justify-between">
                  <div>
                    {additionalHeaderProp?.isCallActive ? (
                      <Text variant={Text.VARIANTS.XS}>
                        {`Call Duration: ${formatCallDuration(additionalHeaderProp?.callDurationSec || 0)}`}
                      </Text>
                    ) : (
                      <div className="flex items-center">
                        {additionalHeaderProp?.callDurationSec && additionalHeaderProp?.callDurationSec > 0 ? (
                          <>
                            <Text
                              variant={Text.VARIANTS.XS}
                              weight={Text.WEIGHTS.SEMI_BOLD}
                              className="mr-4 text-red-700"
                            >
                              Call Ended
                            </Text>
                            <Text variant={Text.VARIANTS.XS}>
                              {`(Duration: ${formatCallDuration(additionalHeaderProp?.callDurationSec || 0)})`}
                            </Text>
                          </>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
            {categoryChips ? (
              <div className="flex flex-wrap items-center justify-end gap-4">{categoryChips}</div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  const emailCommunication = isEmailCommunication(communication) ? communication : null;
  const adjusterRecipientTypeDesc = get(RECIPIENTS_TYPES, emailCommunication?.adjuster_recipient_type || '', '');
  const withAdjusterRecipientType = !!adjusterRecipientTypeDesc && emailCommunication?.adjuster_recipient_type !== 'to';

  return (
    <>
      <CommunicationCardHeader
        communicationId={communication?.id}
        communicationClaimId={communicationClaimId}
        channel={channel}
        direction={direction}
        contactId={contact?.id}
        contactFullName={contact?.full_name}
        contactRole={contact?.role}
        isView={isView}
        onCommunicationUpdate={onCommunicationUpdate}
        {...additionalHeaderProp}
      />
      {isView ? (
        <>
          <div className="flex items-center justify-center">
            <Text variant={Text.VARIANTS.SM}>{communicationIdentifier}</Text>
          </div>
          <div className="flex items-center justify-center">
            <Heading variant={Heading.TYPES.H3}>
              {communication.datetime ? serverDateTimeToLocal(communication.datetime) : ''}
            </Heading>
          </div>
          <div className="flex items-center justify-center">
            <Tooltip
              title={emailCommunication?.internal_email_address || ''}
              disabled={!emailCommunication?.internal_email_address}
            >
              <span className={classes.containerCentered}>
                {`${direction === 'Outgoing' ? 'from:' : 'to:'} ${communication.adjuster} `} &nbsp;
                {withAdjusterRecipientType ? (
                  <Caption variant={Caption.VARIANTS.LABEL}>({adjusterRecipientTypeDesc})</Caption>
                ) : null}
              </span>
            </Tooltip>
          </div>
        </>
      ) : null}
      {categoryChips ? <div className="mt-8 flex">{categoryChips}</div> : null}
    </>
  );
};

export default CommunicationFullHeader;
